import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Grid, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Button, Chip, TextField
} from '@mui/material';
import { Refresh, TrendingUp, AccountBalance, Person, Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useApi from '../../utils/useApi';
import ChangesTable from './ChangesTable';

const RecentChanges = () => {
  const { Get, getHost } = useApi();
  const [filterType, setFilterType] = useState('hours');
  const [hours, setHours] = useState(24);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [model, setModel] = useState('all');
  const [isDhruvInv, setIsDhruvInv] = useState('all');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchRecentChanges = async () => {
    setLoading(true);
    try {
      let url = `${getHost()}/api/v1/recentChanges/?`;
      
      if (filterType === 'hours') {
        url += `hours=${hours}`;
      } else if (filterType === 'dateRange') {
        if (fromDate) url += `from_date=${fromDate}`;
        if (toDate) url += `&to_date=${toDate}`;
      }
      
      if (model !== 'all') {
        url += `&model=${model}`;
      }
      
      if (isDhruvInv !== 'all') {
        url += `&is_dhruv_inv=${isDhruvInv}`;
      }

      const response = await Get(url);
      console.log('📊 Recent changes:', response);
      setData(response);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('❌ Failed to fetch recent changes:', error);
      toast.error('Failed to fetch recent changes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentChanges();
  }, []);

  const downloadTransactionsCSV = () => {
    if (!data?.transactions?.recent || data.transactions.recent.length === 0) {
      toast.info('No transactions to download');
      return;
    }

    const headers = ['ID', 'Type', 'Amount', 'Subscription No', 'Group', 'Timestamp'];
    const rows = data.transactions.recent.map(t => [
      t.id,
      t.transaction_type,
      t.amount,
      t.investment__subscription__subscription_no,
      t.investment__subscription__group__title,
      new Date(t.timestamp).toLocaleString()
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Transactions downloaded');
  };

  return (
    <Container maxWidth="xl">
      <Box py={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Recent Changes
          </Typography>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchRecentChanges}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter Type</InputLabel>
                <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Filter Type">
                  <MenuItem value="hours">Last N Hours</MenuItem>
                  <MenuItem value="dateRange">Date Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {filterType === 'hours' && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Time Range</InputLabel>
                  <Select value={hours} onChange={(e) => setHours(e.target.value)} label="Time Range">
                    <MenuItem value={12}>Last 12 hours</MenuItem>
                    <MenuItem value={24}>Last 24 hours</MenuItem>
                    <MenuItem value={48}>Last 48 hours</MenuItem>
                    <MenuItem value={72}>Last 72 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {filterType === 'dateRange' && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Model Type</InputLabel>
                <Select value={model} onChange={(e) => setModel(e.target.value)} label="Model Type">
                  <MenuItem value="all">All Models</MenuItem>
                  <MenuItem value="transaction">Transactions Only</MenuItem>
                  <MenuItem value="investment">Investments Only</MenuItem>
                  <MenuItem value="user">Users Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Investment Type</InputLabel>
                <Select value={isDhruvInv} onChange={(e) => setIsDhruvInv(e.target.value)} label="Investment Type">
                  <MenuItem value="all">All Investments</MenuItem>
                  <MenuItem value="false">P.Chandra Investment</MenuItem>
                  <MenuItem value="true">Dhruv Investment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={filterType === 'dateRange' ? 12 : 3}>
              <Button variant="contained" fullWidth onClick={fetchRecentChanges} disabled={loading}>
                Apply Filters
              </Button>
            </Grid>
          </Grid>

          {data && (
            <Box mt={2}>
              <Chip label={`${data.time_range}`} color="primary" />
              <Typography variant="caption" sx={{ ml: 2 }}>
                From: {data.from_time} | To: {data.to_time}
              </Typography>
            </Box>
          )}
        </Paper>

        {data && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {data.transactions && (
                <Grid item xs={12} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Transactions
                          </Typography>
                          <Typography variant="h4">{data.transactions.count}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Total: ₹{data.transactions.total_amount?.toFixed(2)}
                          </Typography>
                          <Button size="small" startIcon={<Download />} onClick={downloadTransactionsCSV} sx={{ mt: 1 }}>
                            CSV
                          </Button>
                        </Box>
                        <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {data.investments && (
                <Grid item xs={12} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Investments
                          </Typography>
                          <Typography variant="h4">{data.investments.count}</Typography>
                        </Box>
                        <AccountBalance sx={{ fontSize: 40, color: 'success.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {data.subscriptions && (
                <Grid item xs={12} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Subscriptions
                          </Typography>
                          <Typography variant="h4">{data.subscriptions.count}</Typography>
                        </Box>
                        <AccountBalance sx={{ fontSize: 40, color: 'warning.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {data.groups && (
                <Grid item xs={12} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Groups
                          </Typography>
                          <Typography variant="h4">{data.groups.count}</Typography>
                        </Box>
                        <AccountBalance sx={{ fontSize: 40, color: 'secondary.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {data.users && (
                <Grid item xs={12} md={2.4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography color="textSecondary" gutterBottom>
                            Users
                          </Typography>
                          <Typography variant="h4">{data.users.count}</Typography>
                        </Box>
                        <Person sx={{ fontSize: 40, color: 'info.main' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <ChangesTable data={data} />
          </>
        )}
      </Box>
    </Container>
  );
};

export default RecentChanges;
